import { Button } from 'tuui';
import { Radio } from 'taboola-ultimate-ui';
import { RadioGroupField } from 'modules/campaigns/components/Form';
import { CONVERSION_LOCATION } from 'modules/codeless-events/consts/conversionFlowConstants';
import { useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { STEP_IDS } from '../../config/stepIds';
import { NewEventHeader } from '../Headers/NewEventHeader';
import styles from './buttonConversionLocationSelector.module.scss';

export const locationOptions = [
    {
        value: CONVERSION_LOCATION.SPECIFIC_PAGE,
        title: (
            <FormattedMessage
                id="codeless.conversion.type.button.location.on.this.page"
                defaultMessage="On this page"
            />
        ),
        description: (
            <FormattedMessage
                id="codeless.conversion.type.button.location.on.this.page.description"
                defaultMessage="Manually pick one or more buttons that appear on this page only."
            />
        ),
    },
    {
        value: CONVERSION_LOCATION.CROSS_SITE,
        title: (
            <FormattedMessage
                id="codeless.conversion.type.button.location.across.your.site"
                defaultMessage="Across your site"
            />
        ),
        description: (
            <FormattedMessage
                id="codeless.conversion.type.button.location.across.your.site.description"
                defaultMessage="Use a single text or URL rule to track multiple buttons across your site—all grouped in one conversion."
            />
        ),
    },
];

export const ButtonConversionLocationSelector = ({ navigateToStepId, handleCancelWizard }) => {
    const { value: locationScope, onChange: setLocationScope } = useFormFieldValue({
        field: 'codelessDraftEvent.locationScope',
    });

    const navigateToPreviousStep = () => navigateToStepId(STEP_IDS.CONVERSION_TYPE_SETUP);

    const navigateToNextStep = () => {
        const nextStepId =
            locationScope === CONVERSION_LOCATION.SPECIFIC_PAGE
                ? STEP_IDS.BUTTON_ANALYZER_STEP
                : STEP_IDS.GLOBAL_BUTTON_SETUP;
        navigateToStepId(nextStepId);
    };

    return (
        <div className={styles['container']}>
            <NewEventHeader handlerArrowLeftButton={navigateToPreviousStep} />
            <RadioGroupField
                groupTitle={
                    <FormattedMessage
                        id="codeless.conversion.location.track.buttons.header"
                        defaultMessage="Where do you want to track buttons?"
                    />
                }
                radioGroupName="conversionLocationSelection"
                onChange={setLocationScope}
                selectedValue={locationScope}
                data-metrics-component="button-conversion-location-selector"
                className={styles['group']}
            >
                {locationOptions.map(({ value: optionValue, title, description }) => (
                    <Radio
                        labelClassName={styles['radio']}
                        descriptionClassName={styles['description']}
                        aria-label={`${optionValue}`}
                        key={`button-conversion-location-selector-${optionValue}`}
                        value={optionValue}
                        title={title}
                        description={description}
                        data-metrics-event-name={GTM_EVENTS.USABILITY}
                        data-metrics-value={optionValue}
                        inputClassName={styles['input']}
                    />
                ))}
            </RadioGroupField>
            <footer className={styles['footer']}>
                <Button
                    onClick={handleCancelWizard}
                    variant={Button.variant.outline}
                    size={Button.size.md}
                    aria-label="Cancel"
                >
                    <FormattedMessage id="codeless.cancel.button" defaultMessage="Cancel" />
                </Button>
                <Button
                    onClick={navigateToNextStep}
                    variant={Button.variant.primary}
                    size={Button.size.md}
                    className={styles['next-button']}
                    aria-label="Next"
                >
                    <FormattedMessage id="codeless.next.button" defaultMessage="Next" />
                </Button>
            </footer>
        </div>
    );
};
